import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/utils/constants";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Check, X, Clock, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-800",
};

const IDENTITY_KEY = "rota_my_identity";

export const HolidayRequestDialog = ({ open, onClose, teamMembers, editMode, authPassword }) => {
  const [tab, setTab] = useState("request"); // "request" or "manage"
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Request form state
  const [selectedMember, setSelectedMember] = useState(() => {
    try { return localStorage.getItem(IDENTITY_KEY) || ""; } catch (e) { console.error('localStorage read error:', e); return ""; }
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isConcrete, setIsConcrete] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState(null);

  // Manage state
  const [denyDialogId, setDenyDialogId] = useState(null);
  const [denyReason, setDenyReason] = useState("");
  const [processing, setProcessing] = useState(null);
  const [filterMember, setFilterMember] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showHistory, setShowHistory] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageYear, setUsageYear] = useState(new Date().getFullYear());
  const [myIdentity, setMyIdentity] = useState(() => {
    try { return localStorage.getItem(IDENTITY_KEY) || ""; } catch (e) { console.error('localStorage read error:', e); return ""; }
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/holiday/requests`);
      setRequests(resp.data);
    } catch (e) { console.error('Failed to fetch holiday requests:', e); }
    setLoading(false);
  }, []);

  const fetchBalance = useCallback(async (memberId) => {
    if (!memberId) { setBalance(null); return; }
    try {
      const resp = await axios.get(`${API}/holiday/balance/${memberId}`);
      setBalance(resp.data);
    } catch { setBalance(null); }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRequests();
      setTab(editMode ? "manage" : "request");
      // Restore saved identity
      const saved = localStorage.getItem(IDENTITY_KEY) || "";
      setMyIdentity(saved);
      setSelectedMember(saved);
    }
  }, [open, fetchRequests, editMode]);

  useEffect(() => {
    if (selectedMember) fetchBalance(selectedMember);
  }, [selectedMember, fetchBalance]);

  useEffect(() => {
    if (open && editMode) {
      axios.get(`${API}/holiday/usage?year=${usageYear}`)
        .then(resp => setUsage(resp.data))
        .catch(e => console.error('Failed to fetch holiday usage:', e));
    }
  }, [open, editMode, usageYear]);

  const handleMemberChange = (memberId) => {
    setSelectedMember(memberId);
    setMyIdentity(memberId);
    try { localStorage.setItem(IDENTITY_KEY, memberId); } catch (e) { console.error('localStorage write error:', e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember || !startDate) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/holiday/request`, {
        member_id: selectedMember,
        start_date: startDate,
        end_date: endDate || startDate,
        is_concrete: isConcrete,
        reason,
      });
      toast.success("Holiday request submitted");
      if (resp.data?.warning) toast.warning(resp.data.warning, { duration: 8000 });
      setStartDate("");
      setEndDate("");
      setIsConcrete(false);
      setReason("");
      fetchRequests();
      fetchBalance(selectedMember);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    }
    setSubmitting(false);
  };

  const handleReview = async (requestId, action, denialReason) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/holiday/requests/${requestId}`, {
        action,
        denial_reason: denialReason || null,
      }, { headers: { "X-Auth-Password": authPassword } });
      toast.success(`Request ${action === 'approve' ? 'approved' : 'denied'}`);
      setDenyDialogId(null);
      setDenyReason("");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process request");
    }
    setProcessing(null);
  };

  const handleDelete = async (requestId) => {
    try {
      await axios.delete(`${API}/holiday/requests/${requestId}`, {
        headers: { "X-Auth-Password": authPassword },
      });
      toast.success("Request cancelled");
      fetchRequests();
    } catch {
      toast.error("Failed to cancel request");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      });
    } catch { return dateStr; }
  };

  const getDays = (start, end) => {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    return Math.round((e - s) / 86400000) + 1;
  };

  const pendingRequests = requests.filter(r => {
    if (r.status !== "pending") return false;
    if (!editMode && myIdentity && r.member_id !== myIdentity) return false;
    return true;
  });
  const historyRequests = requests.filter(r => {
    if (r.status === "pending") return false;
    if (!editMode && myIdentity && r.member_id !== myIdentity) return false;
    return true;
  });

  const myName = teamMembers.find(m => m.id === myIdentity)?.name;

  const filteredHistory = historyRequests.filter(r => {
    if (filterMember !== "all" && r.member_id !== filterMember) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2d2d2d]">
            <Calendar className="h-5 w-5" />
            Holiday Requests
          </DialogTitle>
          <DialogDescription>Request time off or manage holiday requests</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="holiday-tabs">
          <button
            onClick={() => setTab("request")}
            className={`flex-1 text-sm py-1.5 px-3 rounded-md transition-colors ${tab === "request" ? "bg-white shadow-sm font-medium text-[#2d2d2d]" : "text-gray-500 hover:text-gray-700"}`}
            data-testid="holiday-tab-request"
          >
            Request
          </button>
          <button
            onClick={() => setTab("manage")}
            className={`flex-1 text-sm py-1.5 px-3 rounded-md transition-colors ${tab === "manage" ? "bg-white shadow-sm font-medium text-[#2d2d2d]" : "text-gray-500 hover:text-gray-700"}`}
            data-testid="holiday-tab-manage"
          >
            {editMode ? "Manage" : "My Requests"}
            {pendingRequests.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-5 w-5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                {pendingRequests.length}
              </span>
            )}
          </button>
        </div>

        {/* REQUEST TAB */}
        {tab === "request" && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="holiday-request-form">
            {/* Team Member */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Team Member</label>
              <Select value={selectedMember} onValueChange={handleMemberChange}>
                <SelectTrigger data-testid="holiday-member-select">
                  <SelectValue placeholder="Select your name" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Balance display */}
            {balance?.concrete && (() => {
              const c = balance.concrete;
              const u = balance.unconcrete;
              const cOver = c.used >= c.limit;
              const cClose = !cOver && c.remaining <= 2;
              const uOver = u.used >= u.limit;
              const uClose = !uOver && u.remaining <= 3;
              return (
                <div className="p-3 bg-gray-50 rounded-lg space-y-2" data-testid="holiday-balance">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${cOver ? "text-red-600" : cClose ? "text-amber-600" : "text-[#2d2d2d]"}`} data-testid="balance-concrete-used">{c.used}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">concrete days</div>
                    </div>
                    <div className="h-8 w-px bg-gray-200" />
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${uOver ? "text-red-600" : uClose ? "text-amber-600" : "text-[#2d2d2d]"}`} data-testid="balance-unconcrete-used">{u.used}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">un-concrete days</div>
                    </div>
                    <div className="h-8 w-px bg-gray-200" />
                    <div className="text-xs text-gray-500">used or requested in {balance.year}</div>
                  </div>
                  {cOver && (
                    <p className="text-xs text-red-600 font-medium" data-testid="balance-concrete-warning">Concrete limit reached — {c.used} of {c.limit} days used</p>
                  )}
                  {cClose && (
                    <p className="text-xs text-amber-600 font-medium" data-testid="balance-concrete-warning">Getting close — only {c.remaining} concrete day{c.remaining !== 1 ? "s" : ""} left of {c.limit} this year</p>
                  )}
                  {uOver && (
                    <p className="text-xs text-red-600 font-medium" data-testid="balance-unconcrete-warning">Un-concrete limit reached — {u.used} of {u.limit} days used</p>
                  )}
                  {uClose && (
                    <p className="text-xs text-amber-600 font-medium" data-testid="balance-unconcrete-warning">Getting close — {u.used} of {u.limit} un-concrete days used this year</p>
                  )}
                </div>
              );
            })()}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); if (!endDate) setEndDate(e.target.value); }}
                  required
                  data-testid="holiday-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  min={startDate}
                  data-testid="holiday-end-date"
                />
              </div>
            </div>
            {startDate && endDate && (
              <p className="text-xs text-gray-500 -mt-2">
                {getDays(startDate, endDate)} day{getDays(startDate, endDate) > 1 ? 's' : ''} requested
              </p>
            )}

            {/* Concrete toggle */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-2 block">Type</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setIsConcrete(false)}
                  className={`p-3 rounded-lg border text-left transition-all ${!isConcrete ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid="holiday-type-unconcrete"
                >
                  <div className="text-sm font-medium text-[#2d2d2d]">Un-concrete</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">Flexible — can be changed</div>
                </button>
                <button
                  type="button"
                  onClick={() => setIsConcrete(true)}
                  className={`p-3 rounded-lg border text-left transition-all ${isConcrete ? "border-green-400 bg-green-50 ring-1 ring-green-300" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid="holiday-type-concrete"
                >
                  <div className="text-sm font-medium text-[#2d2d2d]">Concrete</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">Locked in — cannot be changed</div>
                </button>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Reason</label>
              <Input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g., Holiday abroad, dentist appointment"
                data-testid="holiday-reason"
              />
            </div>

            <Button
              type="submit"
              disabled={!selectedMember || !startDate || submitting}
              className="w-full bg-[#605e5e] hover:bg-[#4a4848] text-white"
              data-testid="holiday-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </form>
        )}

        {/* MANAGE TAB */}
        {tab === "manage" && (
          <div className="space-y-4" data-testid="holiday-manage">
            {/* Per-member usage overview (admin) */}
            {editMode && (
              <div className="border border-gray-200 rounded-lg p-3" data-testid="holiday-usage-section">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowUsage(!showUsage)}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700"
                    data-testid="holiday-usage-toggle"
                  >
                    Days Used {usageYear}
                    {showUsage ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  {showUsage && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => setUsageYear(usageYear - 1)} className="h-6 w-6 rounded hover:bg-gray-100 text-gray-500" data-testid="holiday-usage-prev-year">‹</button>
                      <span className="text-xs font-medium text-[#2d2d2d] w-10 text-center">{usageYear}</span>
                      <button type="button" onClick={() => setUsageYear(usageYear + 1)} className="h-6 w-6 rounded hover:bg-gray-100 text-gray-500" data-testid="holiday-usage-next-year">›</button>
                    </div>
                  )}
                </div>
                {showUsage && usage && (
                  <div className="mt-2 space-y-1">
                    <div className="grid grid-cols-3 text-[10px] text-gray-400 uppercase tracking-wide px-1">
                      <span>Member</span>
                      <span className="text-center">Concrete</span>
                      <span className="text-center">Un-concrete</span>
                    </div>
                    {usage.members.map(m => {
                      const cOver = m.concrete_used >= usage.concrete_limit;
                      const cClose = !cOver && m.concrete_used >= usage.concrete_limit - 2;
                      const uOver = m.unconcrete_used >= usage.unconcrete_limit;
                      const uClose = !uOver && m.unconcrete_used >= usage.unconcrete_limit - 3;
                      return (
                        <div key={m.member_id} className="grid grid-cols-3 text-xs py-1 px-1 rounded odd:bg-gray-50 items-center" data-testid={`holiday-usage-row-${m.member_id}`}>
                          <span className="font-medium text-[#2d2d2d] truncate">{m.member_name}</span>
                          <span className={`text-center font-medium ${cOver ? "text-red-600" : cClose ? "text-amber-600" : "text-gray-600"}`}>
                            {cOver || cClose ? `${m.concrete_used}/${usage.concrete_limit}` : m.concrete_used}
                          </span>
                          <span className={`text-center font-medium ${uOver ? "text-red-600" : uClose ? "text-amber-600" : "text-gray-600"}`}>
                            {uOver || uClose ? `${m.unconcrete_used}/${usage.unconcrete_limit}` : m.unconcrete_used}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Identity selector for non-edit mode */}
            {!editMode && (
              <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg" data-testid="holiday-identity-selector">
                <span className="text-xs text-gray-500 whitespace-nowrap">Showing for:</span>
                <Select value={myIdentity || "none"} onValueChange={(v) => {
                  const val = v === "none" ? "" : v;
                  setMyIdentity(val);
                  setSelectedMember(val);
                  try { if (val) localStorage.setItem(IDENTITY_KEY, val); } catch (e) { console.error('localStorage write error:', e); }
                }}>
                  <SelectTrigger className="h-7 text-xs flex-1" data-testid="holiday-identity-dropdown">
                    <SelectValue placeholder="Select your name" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!editMode && !myIdentity && (
              <div className="text-center py-6 text-sm text-gray-400">
                Select your name above to see your requests
              </div>
            )}

            {(editMode || myIdentity) && loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
            ) : (editMode || myIdentity) && (
              <>
                {/* Pending Requests */}
                {pendingRequests.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Pending ({pendingRequests.length})
                    </h3>
                    <div className="space-y-2">
                      {pendingRequests.map(req => (
                        <div key={req.id} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid={`holiday-pending-${req.id}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-sm text-[#2d2d2d]">{req.member_name}</div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {req.start_date === req.end_date
                                  ? formatDate(req.start_date)
                                  : `${formatDate(req.start_date)} — ${formatDate(req.end_date)}`
                                }
                                <span className="ml-1.5 text-gray-400">
                                  ({getDays(req.start_date, req.end_date)} day{getDays(req.start_date, req.end_date) > 1 ? 's' : ''})
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className={`text-[10px] ${req.is_concrete ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}`}>
                                  {req.is_concrete ? "Concrete" : "Un-concrete"}
                                </Badge>
                                {req.reason && <span className="text-xs text-gray-500">{req.reason}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              <Clock className="h-3.5 w-3.5 text-amber-500" />
                            </div>
                          </div>

                          {/* Admin actions */}
                          {editMode && (
                            <div className="flex gap-2 mt-3 pt-2 border-t border-amber-200">
                              {denyDialogId === req.id ? (
                                <div className="flex-1 space-y-2">
                                  <Input
                                    placeholder="Reason for denial..."
                                    value={denyReason}
                                    onChange={e => setDenyReason(e.target.value)}
                                    className="h-8 text-sm"
                                    autoFocus
                                    data-testid="holiday-deny-reason"
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="h-7 text-xs flex-1"
                                      onClick={() => handleReview(req.id, 'deny', denyReason)}
                                      disabled={processing === req.id}
                                      data-testid="holiday-deny-confirm"
                                    >
                                      Confirm Deny
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => { setDenyDialogId(null); setDenyReason(""); }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white flex-1"
                                    onClick={() => handleReview(req.id, 'approve')}
                                    disabled={processing === req.id}
                                    data-testid={`holiday-approve-${req.id}`}
                                  >
                                    {processing === req.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50 flex-1"
                                    onClick={() => setDenyDialogId(req.id)}
                                    data-testid={`holiday-deny-${req.id}`}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Deny
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pendingRequests.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-400">
                    {editMode ? "No pending requests" : "No pending requests for you"}
                  </div>
                )}

                {/* History */}
                <div>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700"
                    data-testid="holiday-toggle-history"
                  >
                    History ({historyRequests.length})
                    {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>

                  {showHistory && (
                    <div className="mt-2 space-y-2">
                      {/* Filters */}
                      <div className="flex gap-2">
                        <Select value={filterMember} onValueChange={setFilterMember}>
                          <SelectTrigger className="h-7 text-xs flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Members</SelectItem>
                            {teamMembers.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                          <SelectTrigger className="h-7 text-xs w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="denied">Denied</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {filteredHistory.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">No history</p>
                      ) : (
                        filteredHistory.map(req => (
                          <div key={req.id} className="border border-gray-200 rounded-lg p-3" data-testid={`holiday-history-${req.id}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="font-medium text-sm text-[#2d2d2d]">{req.member_name}</div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {req.start_date === req.end_date
                                    ? formatDate(req.start_date)
                                    : `${formatDate(req.start_date)} — ${formatDate(req.end_date)}`
                                  }
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={`text-[10px] ${STATUS_STYLES[req.status]}`}>
                                    {req.status}
                                  </Badge>
                                  <Badge variant="outline" className={`text-[10px] ${req.is_concrete ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}`}>
                                    {req.is_concrete ? "Concrete" : "Un-concrete"}
                                  </Badge>
                                </div>
                                {req.denial_reason && (
                                  <p className="text-xs text-red-500 mt-1">Reason: {req.denial_reason}</p>
                                )}
                              </div>
                              {editMode && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                  onClick={() => handleDelete(req.id)}
                                  data-testid={`holiday-delete-${req.id}`}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
